// canvas/hand.js: per-seat hand renderer (plan-m2.md v2 M2.c-2).
//
// renderHand draws one seat's cards: a face-up fan for the viewer's own
// seat (input.viewerSeat, default lua 1), small face-down sprites for
// the other seats. Returns hit zones for the viewer's hand so App.jsx
// can overlay transparent <button> elements for keyboard + screen-reader
// access (plan v2 codex #12). With a layout rotated by
// rotateSeatsForViewer, the viewer's seat is always the bottom slot.
//
// State shape (subset relevant here):
//   {
//     current_state: string,
//     trump_suit:    'S'|'H'|'D'|'C'|null,
//     hands:         { 1:[card_id...], 2:[...], 3:[...], 4:[...] },
//     legal_plays?:  { 1:[card_id...], ... } | undefined,
//     sitting_out_seat?: number|null,
//   }
//
// input_state:
//   { hoveredCardId?: string|null, focusedCardId?: string|null }
//
// Returns: Array<{card_id, x, y, w, h, legal, bower}>, only for seat 1.
// For other seats, returns []. Hit zones use UNTRANSFORMED canvas coords,
// so callers can position DOM overlays via `getBoundingClientRect()`
// offsets.

import { renderCard, isBower, isTrumpCard } from './cards.js';

// 1.08 hover lift per plan AC #4 / synthesis §4.4. Implemented here as a
// y-offset rather than a CSS scale so we stay inside one canvas redraw
// (the canvas owns visuals, DOM owns accessibility, see M2.c-2 codex #12).
const HOVER_LIFT_PX = 12;
// Slight scale-up for hovered card. Drawn by inflating its rect; the
// underlying renderCard uses w/h directly.
const HOVER_SCALE = 1.08;

/**
 * Compute the (x,y) positions of the 5 cards in a fan along the seat's
 * handArea. Returns an array of {x, y, w, h, cardIdx}, caller maps to
 * actual card IDs.
 *
 * Horizontal fans (large + small) start at handArea.x and advance by
 * (cardW - overlap). Vertical stacks start at handArea.y and advance by
 * `overlap` (a small stagger so the stack reads as multiple cards).
 *
 * @param {import('./layout.js').HandArea} area
 * @param {number} cardCount
 */
export function computeFanPositions(area, cardCount) {
  const positions = [];
  if (area.orientation === 'horizontal-large' ||
      area.orientation === 'horizontal-small') {
    const step = area.cardW - area.overlap;
    for (let i = 0; i < cardCount; i++) {
      positions.push({
        x: area.x + i * step,
        y: area.y,
        w: area.cardW,
        h: area.cardH,
        cardIdx: i,
      });
    }
  } else {
    // stack: vertical, with small stagger
    for (let i = 0; i < cardCount; i++) {
      positions.push({
        x: area.x,
        y: area.y + i * area.overlap,
        w: area.cardW,
        h: area.cardH,
        cardIdx: i,
      });
    }
  }
  return positions;
}

/**
 * Render one seat's hand. Returns hit zones for seat 1 (player); empty
 * array for other seats.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('./layout.js').SeatBox} seat
 * @param {{
 *   current_state?: string,
 *   trump_suit?:    string|null,
 *   hands?:         Record<number, string[]>,
 *   legal_plays?:   Record<number, string[]>,
 *   sitting_out_seat?: number|null,
 * }} state
 * @param {{
 *   hoveredCardId?: string|null,
 *   focusedCardId?: string|null,
 *   viewerSeat?: number,
 * }} [input]
 * @returns {Array<{card_id:string, x:number, y:number, w:number, h:number, legal:boolean, bower:'right'|'left'|null}>}
 */
export function renderHand(ctx, seat, state, input = {}) {
  const hands = state.hands || {};
  const hand = hands[seat.id] || [];
  if (hand.length === 0) return [];
  if (state.sitting_out_seat === seat.id) return [];

  const positions = computeFanPositions(seat.handArea, hand.length);
  const isPlayer = seat.id === (input.viewerSeat ?? 1);
  const trump = state.trump_suit || null;

  // Legal-card set for grayed overlay (player only; trick_play only).
  const playerLegal = (state.current_state === 'trick_play' && state.legal_plays)
    ? new Set(state.legal_plays[seat.id] || [])
    : null;

  /** @type {Array<{card_id:string, x:number, y:number, w:number, h:number, legal:boolean, bower:'right'|'left'|null}>} */
  const hitZones = [];

  for (let i = 0; i < hand.length; i++) {
    const cardId = hand[i];
    const pos = positions[i];
    const faceUp = isPlayer;
    const bower = faceUp ? isBower(cardId, trump) : null;

    // Legal only matters for face-up; default to legal=true otherwise.
    const legal = (playerLegal === null) ? true : playerLegal.has(cardId);
    const grayed = isPlayer && playerLegal !== null && !legal;

    const hovered = isPlayer && input.hoveredCardId === cardId;
    const focused = isPlayer && input.focusedCardId === cardId;

    // M5 #6, hover lift only fires on LEGAL cards. Illegal cards stay
    // flat (no lift, no scale), the grayed overlay above already
    // signals unplayability. Synthesis §3 Pillar 2 / Balatro MUST #5.
    let { x, y, w, h } = pos;
    const liftEligible = (hovered || focused) && legal;
    if (liftEligible) {
      const liftY = HOVER_LIFT_PX;
      const dw = Math.round(w * (HOVER_SCALE - 1));
      const dh = Math.round(h * (HOVER_SCALE - 1));
      x = x - Math.round(dw / 2);
      y = y - liftY;
      w = w + dw;
      h = h + dh;
    }

    renderCard(ctx, cardId, x, y, w, h, {
      face_up: faceUp,
      grayed,
      // M5 #6, illegal cards never show the hover ring (no lift =
      // no hover affordance). Legal hover keeps the existing ring.
      hover: hovered && legal,
      focused: focused && legal,
      bower,
      trump: faceUp && isTrumpCard(cardId, trump),
    });

    if (isPlayer) {
      // Hit zone uses the UNLIFTED rect, buttons stay put when card lifts.
      hitZones.push({
        card_id: cardId,
        x: pos.x,
        y: pos.y,
        w: pos.w,
        h: pos.h,
        legal,
        bower,
      });
    }
  }

  return hitZones;
}

/**
 * Render every seat's hand and return the viewer's hit zones
 * (input.viewerSeat, default lua 1). Convenience wrapper used by
 * App.jsx; tests can call renderHand per-seat for targeted assertions.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('./layout.js').TableLayout} layout
 * @param {object} state
 * @param {object} [input]
 */
export function renderAllHands(ctx, layout, state, input = {}) {
  const viewerSeat = input.viewerSeat ?? 1;
  let playerHits = [];
  for (const seat of layout.seats) {
    const hits = renderHand(ctx, seat, state, input);
    if (seat.id === viewerSeat) playerHits = hits;
  }
  return playerHits;
}

export const _internals = { HOVER_LIFT_PX, HOVER_SCALE };
